import secureLs from '@/libs/secureLs'

let userDataCache = null

/**
 * Mengambil data pengguna dari cache lokal (secureLs).
 * Jika `forcedRefresh` bernilai `true`, data akan diambil ulang dari secure storage.
 *
 * @function
 * @param {Object} [options={}] - Opsi untuk kontrol perilaku fungsi.
 * @param {boolean} [options.forcedRefresh=false] - Jika `true`, memaksa refresh data dari secureLs.
 * @returns {Object} Objek data pengguna yang telah disusun ulang dan disimpan di cache lokal.
 *
 * @example
 * const user = getUserData();
 * console.log(user.id);
 *
 * // Untuk mengambil ulang data meskipun sudah ada cache:
 * const refreshedUser = getUserData({ forcedRefresh: true });
 */
export function getUserData({ forcedRefresh = false } = {}) {
  if (!userDataCache || forcedRefresh) {
    const userData = secureLs.getItem('userData')
    userDataCache = {
      id: userData.id,
      is_kompack: userData.is_kompack,
      is_onboarding_kompack: userData.is_onboarding_kompack,
      partner_detail: {
        ...userData.partner_detail,
      },
    }
  }
  return userDataCache
}

/**
 * Mengecek apakah pengguna adalah pengguna Kompack.
 *
 * @function
 * @param {Object} [options={}] - Opsi untuk kontrol cache.
 * @param {boolean} [options.forcedRefresh=false] - Jika true, memaksa refresh data user dari penyimpanan.
 * @returns {boolean} `true` jika pengguna adalah Kompack (`is_kompack === 1`), `false` jika tidak.
 */
export function isKompack({ forcedRefresh = false } = {}) {
  const userData = getUserData({ forcedRefresh })
  return userData.is_kompack === 1
}

/**
 * Mengecek apakah pengguna masih dalam tahap onboarding Kompack.
 *
 * @function
 * @param {Object} [options={}] - Opsi untuk kontrol cache.
 * @param {boolean} [options.forcedRefresh=false] - Jika true, memaksa refresh data user dari penyimpanan.
 * @returns {boolean} `true` jika pengguna *tidak* sedang onboarding (`is_onboarding_kompack === false`).
 */
export function isOnboardingKompack({ forcedRefresh = false } = {}) {
  const userData = getUserData({ forcedRefresh })
  return userData.is_onboarding_kompack === false
}

/**
 * Mengecek apakah profil bisnis pengguna sudah lengkap.
 * Profil dianggap lengkap jika semua properti penting (`brand_name`, `pic_phone`, `business_location`, `city_code`)
 * tidak bernilai `null`.
 *
 * @function
 * @param {Object} [options={}] - Opsi untuk kontrol cache.
 * @param {boolean} [options.forcedRefresh=false] - Jika true, memaksa refresh data user dari penyimpanan.
 * @returns {boolean} `true` jika profil bisnis lengkap, `false` jika belum.
 */
export function isProfilBisnisLengkap({ forcedRefresh = false } = {}) {
  const userData = getUserData({ forcedRefresh })
  const businessData = userData.partner_detail
  if (
    businessData.brand_name !== null
    && businessData.pic_phone !== null
    && businessData.business_location !== null
    && businessData.city_code !== null) {
    return true
  }
  return false
}

/**
 * Updates the Kompack partner onboarding profile information
 *
 * @param {Object} params - The profile parameters to update
 * @param {string} params.brand_name - The brand/company name
 * @param {string} params.pic_phone - The phone number of the person in charge
 * @param {string} params.business_location - The business location/address
 *
 * @returns {void}
 *
 * @example
 * updateProfileKompackOnboarding({
 *   brand_name: 'My Company',
 *   pic_phone: '+6281234567890',
 *   business_location: 'Jakarta, Indonesia'
 * })
 */
export function updateProfileKompackOnboarding({
  brand_name,
  pic_phone,
  business_location,
}) {
  const userData = getUserData({ forcedRefresh: true })
  secureLs.setItem('userData', JSON.stringify({
    ...userData,
    partner_detail: {
      ...userData.partner_detail,
      brand_name,
      pic_phone,
      business_location,
    },
  }))

  userDataCache = {
    ...userDataCache,
    partner_detail: {
      ...userDataCache.partner_detail,
      brand_name,
      pic_phone,
      business_location,
    },
  }
}

/**
 * Memperbarui profil Kompack
 * @returns {void}
 */
export function updateProfileKompack({
  business_logo,
  brand_name,
  pic_phone,
  business_location,
  city_code,
  partner_category_name,
  business_type_id,
}) {
  const userData = secureLs.getItem('userData')
  secureLs.setItem('userData', JSON.stringify({
    ...userData,
    partner_detail: {
      ...userData.partner_detail,
      business_logo,
      brand_name,
      pic_phone,
      business_location,
      city_code,
      partner_category_name,
      business_type_id,
    },
  }))

  userDataCache = {
    ...userDataCache,
    partner_detail: {
      ...userDataCache.partner_detail,
      business_logo,
      brand_name,
      pic_phone,
      business_location,
      city_code,
      partner_category_name,
      business_type_id,
    },
  }
}

/**
 * Memperbarui status onboarding Kompack
 * @returns {void}
 */
export function updateOnboardingKompackStatus() {
  const userData = getUserData({ forcedRefresh: true })
  secureLs.setItem('userData', JSON.stringify({
    ...userData,
    is_onboarding_kompack: !userData.is_onboarding_kompack,
  }))
  userDataCache = {
    ...userDataCache,
    is_onboarding_kompack: !userDataCache.is_onboarding_kompack,
  }
}
